import React from 'react'
import { FaTable } from 'react-icons/fa'

const VPNPeriodTable = ({ vpnTests, selectedPeriod, formatDate }) => {
  // Helper function to format period dates according to locale
  const formatPeriodDate = (period) => {
    if (!period) return 'N/A'
    
    if (selectedPeriod === 'week') {
      // Handle both "2025W30" and "202530" formats
      let year, week
      const matchW = period.match(/^(\d{4})W(\d{2})$/)
      const matchPlain = period.match(/^(\d{4})(\d{2})$/)
      
      if (matchW) {
        [, year, week] = matchW
      } else if (matchPlain) {
        [, year, week] = matchPlain
      } else {
        return `Week ${period}`
      }
      
      // Create a date for the first day of the week
      const firstDayOfYear = new Date(parseInt(year), 0, 1)
      const firstWeekday = firstDayOfYear.getDay()
      const daysToAdd = (parseInt(week) - 1) * 7 - firstWeekday + 1
      const weekStartDate = new Date(parseInt(year), 0, 1 + daysToAdd)
      const weekEndDate = new Date(weekStartDate)
      weekEndDate.setDate(weekStartDate.getDate() + 6)
      
      const startFormatted = new Intl.DateTimeFormat(navigator.language || 'en-US', {
        month: 'short',
        day: 'numeric'
      }).format(weekStartDate)
      
      const endFormatted = new Intl.DateTimeFormat(navigator.language || 'en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).format(weekEndDate)
      
      return `${startFormatted} - ${endFormatted} (Week ${week})`
    } else {
      // Format month periods (e.g., "2025-06" -> localized month format)
      const match = period.match(/^(\d{4})-(\d{2})$/)
      if (match) {
        const [, year, month] = match
        const date = new Date(parseInt(year), parseInt(month) - 1, 1)
        
        return new Intl.DateTimeFormat(navigator.language || 'en-US', {
          year: 'numeric',
          month: 'long'
        }).format(date)
      }
      return period
    }
  }

  if (vpnTests.length === 0) {
    return (
      <div className='text-center py-12'>
        <FaTable className='w-12 h-12 text-gray-400 mx-auto mb-4' />
        <p className='text-gray-500 text-lg'>No VPN test data found for the selected filters.</p>
      </div>
    )
  }

  return (
    <div className='overflow-x-auto'>
      {selectedPeriod === 'month'
        ? (() => {
            // Group by period, then broadcaster, then VPN
            const periodBroadcasterVpnMap = {}
            vpnTests.forEach((row) => {
              const period = row.period
              const broadcaster = row.broadcaster_name || 'Unknown Broadcaster'
              if (!periodBroadcasterVpnMap[period]) periodBroadcasterVpnMap[period] = {}
              if (!periodBroadcasterVpnMap[period][broadcaster])
                periodBroadcasterVpnMap[period][broadcaster] = {}
              
              // Use VPN name from the data
              let vpnName = row.vpn_name || 'Unknown VPN'
              if (!periodBroadcasterVpnMap[period][broadcaster][vpnName]) {
                periodBroadcasterVpnMap[period][broadcaster][vpnName] = {
                  blocked: row.blocked_tests || 0,
                  failed: row.failed_tests || 0,
                  success: row.successful_tests || 0,
                  total: row.total_tests || 0,
                  success_rate: row.success_rate || 0,
                }
              }
            })
            
            // Sort periods descending
            const sortedPeriods = Object.keys(periodBroadcasterVpnMap).sort((a, b) => b.localeCompare(a))
            return (
              <>
                {sortedPeriods.map((period) => (
                  <div
                    key={period}
                    className='mb-8'
                  >
                    <h3 className='text-lg font-bold mb-2'>Month: {period}</h3>
                    <table className='min-w-full bg-white border border-gray-200 rounded-lg mb-4'>
                      <thead className='bg-gray-50'>
                        <tr>
                          <th className='px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider'>
                            Broadcaster
                          </th>
                          <th className='px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider'>
                            Date
                          </th>
                          <th className='px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider'>
                            BLOCKED
                          </th>
                          <th className='px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider'>
                            FAILED
                          </th>
                          <th className='px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider'>
                            % SUCCESS
                          </th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-gray-200'>
                        {Object.entries(periodBroadcasterVpnMap[period]).map(([broadcaster, vpnStats]) =>
                          Object.entries(vpnStats).map(([vpnName, stats], index) => (
                            <tr
                              key={`${period}-${broadcaster}-${vpnName}-${index}`}
                              className='hover:bg-gray-50'
                            >
                              <td className='px-4 py-3 text-sm text-gray-900 font-medium'>{broadcaster}</td>
                              <td className='px-4 py-3 text-sm text-gray-900'>{formatPeriodDate(period)}</td>
                              <td className='px-4 py-3 text-sm text-gray-900'>{stats.blocked}</td>
                              <td className='px-4 py-3 text-sm text-gray-900'>{stats.failed}</td>
                              <td className='px-4 py-3 text-sm text-gray-900'>{stats.success_rate}%</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                ))}
              </>
            )
          })()
        : (() => {
            // Week view: group by period, broadcaster, and VPN
            const periodBroadcasterVpnMap = {}
            vpnTests.forEach((row) => {
              const period = row.period
              const broadcaster = row.broadcaster_name || 'Unknown Broadcaster'
              if (!periodBroadcasterVpnMap[period]) periodBroadcasterVpnMap[period] = {}
              if (!periodBroadcasterVpnMap[period][broadcaster])
                periodBroadcasterVpnMap[period][broadcaster] = {}
              
              const vpnName = row.vpn_name || 'Unknown VPN'
              periodBroadcasterVpnMap[period][broadcaster][vpnName] = {
                blocked: row.blocked_tests || 0,
                failed: row.failed_tests || 0,
                success: row.successful_tests || 0,
                total: row.total_tests || 0,
                success_rate: row.success_rate || 0,
              }
            })
            
            const sortedPeriods = Object.keys(periodBroadcasterVpnMap).sort((a, b) => b.localeCompare(a))
            return (
              <>
                {sortedPeriods.map((period) => (
                  <div
                    key={period}
                    className='mb-8'
                  >
                    <h3 className='text-lg font-bold mb-2'>Week: {period}</h3>
                    <table className='min-w-full bg-white border border-gray-200 rounded-lg mb-4'>
                      <thead className='bg-gray-50'>
                        <tr>
                          <th className='px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider'>
                            Broadcaster
                          </th>
                          <th className='px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider'>
                            Date
                          </th>
                          <th className='px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider'>
                            BLOCKED
                          </th>
                          <th className='px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider'>
                            FAILED
                          </th>
                          <th className='px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider'>
                            % SUCCESS
                          </th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-gray-200'>
                        {Object.entries(periodBroadcasterVpnMap[period]).map(([broadcaster, vpnStats]) =>
                          Object.entries(vpnStats).map(([vpnName, stats], index) => (
                            <tr
                              key={`${period}-${broadcaster}-${vpnName}-${index}`}
                              className='hover:bg-gray-50'
                            >
                              <td className='px-4 py-3 text-sm text-gray-900 font-medium'>{broadcaster}</td>
                              <td className='px-4 py-3 text-sm text-gray-900'>{formatPeriodDate(period)}</td>
                              <td className='px-4 py-3 text-sm text-gray-900'>{stats.blocked}</td>
                              <td className='px-4 py-3 text-sm text-gray-900'>{stats.failed}</td>
                              <td className='px-4 py-3 text-sm text-gray-900'>{stats.success_rate}%</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                ))}
              </>
            )
          })()}
    </div>
  )
}

export default VPNPeriodTable 